import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { createTiltViewColumns } from "./tilt-view-columns";
import { statusCalculationMethodData360LiveEnum } from "../../constants/commonConstants";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { zoneViewHelper } from "../../helpers/zone-view/zone-view-helper";
import { FETCH_TILT_VIEW_SAGA } from "../../redux/d360Live_Views/types";
import EnterpriseGridCommon from "../AgGrid/Ag-Grid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useTranslation } from "react-i18next";
import ErrorBanner from "../Common/ErrorBanner";
import CommonLoader from "../Loaders/loader";

const Data360LiveTiltViewAgGrid = ({
  projectId,
  projectName,
  weldNumber,
  statusCalculationMethod,
  currentProject,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const state = useSelector((state) => state?.data360LiveViewsReducer);
  const [rows, setRows] = useState([]);

  const tiltViewData = state?.tiltView?.data || [];
  const conversionFactors = state?.tiltView?.conversionFactors;
  const decimalPlaces = state?.tiltView?.decimalPlaces;
  const weldParameterErrors = state?.tiltView?.weldParameterErrors;
  const isLoading = state?.isLoading?.tiltView;

  const getStatusCalculationMethod = (statusCalculationMethod, record) => {
    switch (statusCalculationMethod) {
      case statusCalculationMethodData360LiveEnum.Instantaneous:
        return record.avg_staus_by_instantaneous;
      case statusCalculationMethodData360LiveEnum.AvgByPass:
      case statusCalculationMethodData360LiveEnum.AvgByZone:
      case statusCalculationMethodData360LiveEnum.AvgByTilt:
        return record.avg_tilt_status;
      default:
        return record.avg_staus_by_instantaneous;
    }
  };

  const getStringValue = (value) => (value ? String(value) : "");

  const applyConversionFactor = (value, factor, fieldName) => {
    if (!value || value === "") return "";
    const numValue = parseFloat(value);
    const decimalPlacesForField = decimalPlaces?.[fieldName];
    return isNaN(numValue)
      ? value
      : (numValue * (factor || 1)).toFixed(decimalPlacesForField);
  };

  const createMeasurementObject = (item, prefix) => {
    const conversionFactor = conversionFactors?.[prefix] || 1;
    const decimalPlacesForField = decimalPlaces?.[prefix];

    return {
      low:
        getStringValue(
          item?.[`${prefix}_low`]
            ? (parseFloat(item?.[`${prefix}_low`]) * conversionFactor).toFixed(
                decimalPlacesForField
              )
            : ""
        ) + (item?.[`${prefix}_low_status`] === false ? "-color" : ""),
      avg:
        getStringValue(
          item?.[`${prefix}_avg`]
            ? (parseFloat(item?.[`${prefix}_avg`]) * conversionFactor).toFixed(
                decimalPlacesForField
              )
            : ""
        ) + (item?.[`${prefix}_avg_status`] === false ? "-color" : ""),
      high:
        getStringValue(
          item?.[`${prefix}_high`]
            ? (parseFloat(item?.[`${prefix}_high`]) * conversionFactor).toFixed(
                decimalPlacesForField
              )
            : ""
        ) + (item?.[`${prefix}_high_status`] === false ? "-color" : ""),
    };
  };

  useEffect(() => {
    if (projectId && projectName) {
      dispatch({
        type: FETCH_TILT_VIEW_SAGA,
        payload: {
          project_id: projectId,
          project_name: projectName,
          weld_number: weldNumber,
        },
      });
    }
  }, [dispatch, projectId, projectName, weldNumber]);

  useEffect(() => {
    if (tiltViewData?.length) {
      const mappedData = tiltViewData?.map((item, index) => ({
        sl_no: index + 1,
        status: getStatusCalculationMethod(statusCalculationMethod, item) || "",
        stationNumbers: logDataHelperFunctions.getStationName(
          item?.station_number
        ),
        buyType: item?.bug_type || "",
        type: item?.torch || "",
        name: item?.pass_name || "",
        s_deviceid_event: item?.welduid_s_event_torch || "",
        tiltRange: `${item?.tilt_start} - ${item?.tilt_end}` || "",
        tilt_start: item?.tilt_start || "",
        tilt_end: item?.tilt_end || "",
        weldStartTimeRaw: item?.s_time || "",
        end_time: item?.s_c_time || "",
        weld_uuid: item?.weld_uid || "",

        //for tlog screen
        bug_number: item?.pass_view?.bug_number,
        band_diameter: applyConversionFactor(
          item?.pass_view?.band_diameter,
          conversionFactors?.band_diameter,
          "band_diameter"
        ),
        wire_diameter: applyConversionFactor(
          item?.pass_view?.wire_diameter,
          conversionFactors?.wire_diameter,
          "wire_diameter"
        ),
        logging_distance: applyConversionFactor(
          item?.pass_view?.logging_distance,
          conversionFactors?.logging_distance,
          "logging_distance"
        ),
        pipe_diameter: applyConversionFactor(
          item?.pass_view?.pipe_diameter,
          conversionFactors?.pipe_diameter,
          "pipe_diameter"
        ),

        weld_time: item?.weld_time || "",
        autoIncrement: item?.auto_increment || "-",
        currentA: createMeasurementObject(item, "current"),
        voltV: createMeasurementObject(item, "volts"),
        travelSpeed: createMeasurementObject(item, "travel_speed"),
        trueEnergy: createMeasurementObject(item, "true_energy"),
        heat: createMeasurementObject(item, "heat"),
        wireSpeed: createMeasurementObject(item, "wire_speed"),
        oscillationWidth: createMeasurementObject(item, "oscillation_width"),
        frequency: createMeasurementObject(item, "frequency"),
        target: createMeasurementObject(item, "target"),
        horizontalBias: createMeasurementObject(item, "horizontal_bias"),
        totalWireConsumed: createMeasurementObject(item, "total_wire_consumed"),
        arcImpedance: createMeasurementObject(item, "arc_impedance"),
      }));
      setRows(mappedData);
    }
  }, [tiltViewData, conversionFactors, decimalPlaces]);

  const handleRowClick = (params) => {
    const record = params.data;
    history.push("/data360-live-tlog-data", {
      stationNumbers: record.stationNumbers,
      buyType: record.buyType,
      torch: record.type,
      weld_number: weldNumber,
      s_deviceid_event: record.s_deviceid_event,
      tilt_start: record.tilt_start || 0,
      tilt_end: record.tilt_end,
      view: "Tilt",
      //tlog display
      autoIncrement: record.autoIncrement,
      band_diameter: record.band_diameter,
      logging_distance: record.logging_distance,
      bug_number: record.bug_number,
      wire_diameter: record.wire_diameter,
      pipe_diameter: record.pipe_diameter,
      start_time: record.weldStartTimeRaw,
      end_time: record.end_time,
      weld_time: record.weld_time,

      // gps: record.gps,
      // iwm: record.iwm,
      // isM500: record.isM500,
    });
  };

  const processCellCallback = (params) => {
    if (params?.column?.colDef?.processCellForExport) {
      return params.column.colDef.processCellForExport(params);
    }
    return params.value;
  };

  // Extract error messages from weldParameterErrors
  const getErrorMessages = () => {
    if (
      !weldParameterErrors?.errors ||
      weldParameterErrors.errors.length === 0
    ) {
      return [];
    }

    const errorMessages = [];
    weldParameterErrors.errors.forEach((error) => {
      if (error.parsedErrors?.errorMessages) {
        errorMessages.push(...error.parsedErrors.errorMessages);
      }
    });

    return errorMessages;
  };

  const errorMessages = getErrorMessages();

  return (
    <div className="pass-zone">
      {isLoading ? (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      ) : (
        <>
          <ErrorBanner errors={errorMessages} />
          {rows.length > 0 ? (
            <div className="ag-theme-alpine" style={{ width: "100%" }}>
              <EnterpriseGridCommon
                isLoading={isLoading}
                rows={rows}
                columnDefs={zoneViewHelper.mapZoneViewColumnTitles(
                  createTiltViewColumns(t),
                  logDataHelperFunctions.data360LiveGetReportColumns(
                    currentProject
                  )
                )}
                onRowClicked={handleRowClick}
                exportFileName={"tilt-view"}
                processCellCallback={processCellCallback}
                heightValue={-150}
              />
            </div>
          ) : (
            <div className="slog-NoData">
              <p>{t("sLogData.noDataAvailable")}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Data360LiveTiltViewAgGrid;
